import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        /*
         * hover 에 **테두리**까지 바꾼다 (2026-09-20). 종전에는 바탕만 흰색(255)에서
         * accent(241)로 갔는데 명도차가 5% 뿐이라 담당자가 「아무 반응이 없다」고 했다.
         * 테두리 색 변화는 같은 면적에서 훨씬 잘 읽히고, 글자색을 안 건드려 배지와
         * 부딪히지 않는다. 제 색을 가진 단추(반려 등)는 인스턴스에서 `hover:border-…`
         * 를 덧써 제 색을 지킨다 - cva 가 className 을 마지막에 붙여 그쪽이 이긴다.
         */
        outline:
          "border border-input bg-background shadow-sm hover:border-ring hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        /*
         * 옅은 단추 (2026-09-20). 꽉 찬 바탕이 무거운 자리에 쓴다 - 배지와 같은
         * 「옅은 바탕 + 진한 글자」라서 카드 안에 여럿 서도 화면을 누르지 않는다.
         * 브랜드색이라 회색 테두리 단추(못 누르는 것)와는 한눈에 갈린다.
         */
        soft:
          "border border-primary-subtle-border bg-primary-subtle text-primary-subtle-foreground hover:bg-primary-subtle/60",
        softSuccess:
          "border border-success-subtle-border bg-success-subtle text-success-subtle-foreground hover:bg-success-subtle/60",
        softDestructive:
          "border border-destructive/40 bg-destructive/5 text-destructive hover:border-destructive/60 hover:bg-destructive/10 hover:text-destructive",
        // 「완료 처리」처럼 결과가 성공 상태인 동작에만 쓴다. 그냥 중요한 단추는 default 다.
        success:
          "bg-success text-success-foreground shadow-sm hover:bg-success/90",
        // 테두리가 없으니 바탕만으로 말해야 한다 - accent 보다 한 단계 진하게 간다.
        ghost: "hover:bg-accent hover:text-accent-foreground hover:shadow-sm",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
